import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listContextProducts } from "@/api/assistant";
import { cn } from "@/cn";

export function ContextPicker({
  open,
  onClose,
  selectedIds,
  onChange,
  anchorClassName,
}: {
  open: boolean;
  onClose: () => void;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  anchorClassName?: string;
}) {
  const { data } = useQuery({
    queryKey: ["assistant-context-products"],
    queryFn: listContextProducts,
    enabled: open,
  });

  if (!open) return null;

  const products = data?.products ?? [];

  function toggle(id: string) {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 cursor-default"
        aria-label="Close context picker"
        onClick={onClose}
      />
      <div
        className={cn(
          "absolute bottom-full left-0 z-50 mb-2 w-80 rounded-lg border border-border bg-card p-3 shadow-lg",
          anchorClassName,
        )}
      >
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">
            Choose your context
          </p>
          <Link
            to="/narrative"
            className="text-xs font-medium text-brand hover:underline"
            onClick={onClose}
          >
            Manage products
          </Link>
        </div>
        <p className="mb-2 text-xs font-medium text-muted-foreground">
          Products from Narrative / Content Center
        </p>
        {products.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No products yet. Add products on the Narrative page.
          </p>
        ) : (
          <ul className="max-h-56 space-y-1 overflow-y-auto">
            {products.map((p) => (
              <li key={p.id}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted/20">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(p.id)}
                    onChange={() => toggle(p.id)}
                  />
                  <span className="truncate">{p.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-3 border-t border-border pt-2">
          <p className="text-xs font-medium text-muted-foreground">
            (Old) Legacy context center
          </p>
          <Link
            to="/settings/account"
            className="mt-1 block text-xs text-brand hover:underline"
            onClick={onClose}
          >
            Your personal information
          </Link>
        </div>
      </div>
    </>
  );
}
